import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'emerald' | 'tomato' | 'saffron' | 'plum' | 'cobalt';

const VALID_THEMES: Theme[] = ['emerald', 'tomato', 'saffron', 'plum', 'cobalt'];
const THEME_KEY = 'pp_theme';
const DARK_KEY  = 'pp_dark';

function isValidTheme(v: unknown): v is Theme {
  return VALID_THEMES.includes(v as Theme);
}

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return isValidTheme(stored) ? stored : 'emerald';
  } catch {
    return 'emerald';
  }
}

function readStoredDark(): boolean {
  try {
    const stored = localStorage.getItem(DARK_KEY);
    if (stored !== null) return stored === 'true';
    // Default to dark mode — warm dark is the redesign's brand mode
    return true;
  } catch {
    return true;
  }
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;
  toggleDark: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'emerald',
  setTheme: () => {},
  darkMode: true,
  setDarkMode: () => {},
  toggleDark: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState]       = useState<Theme>(readStoredTheme);
  const [darkMode, setDarkModeState] = useState<boolean>(readStoredDark);

  // Apply accent class on <html> — switches the --accent / --accent-soft / --accent-glow tokens
  useEffect(() => {
    const root = document.documentElement;
    VALID_THEMES.forEach((t) => root.classList.remove(`accent-${t}`));
    root.classList.add(`accent-${theme}`);
  }, [theme]);

  // Apply theme-dark / theme-light on <html> — drives Tailwind's `dark:` variant
  // and the design-system's --bg / --surface / --ink token sets in one swap.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('theme-dark', 'theme-light');
    root.classList.add(darkMode ? 'theme-dark' : 'theme-light');
  }, [darkMode]);

  function setTheme(t: Theme) {
    if (!isValidTheme(t)) return;
    setThemeState(t);
    try { localStorage.setItem(THEME_KEY, t); } catch {}
  }

  function setDarkMode(v: boolean) {
    setDarkModeState(v);
    try { localStorage.setItem(DARK_KEY, String(v)); } catch {}
  }

  function toggleDark() {
    setDarkMode(!darkMode);
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, darkMode, setDarkMode, toggleDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
